import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePlayer } from './PlayerContext';
import './MusicPlayer.css';

const MEDIA_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

// ── Icons ────────────────────────────────────────────────────────────────────
const PlayIcon  = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>;
const PauseIcon = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>;
const NextIcon  = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zM16 6h2v12h-2z"/></svg>;
const PrevIcon  = () => <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/></svg>;
const ShuffleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/>
  </svg>
);
const RepeatIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
  </svg>
);
// ─────────────────────────────────────────────────────────────────────────────

function fmt(s) {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

function MusicPlayer() {
  const { tracks, currentIndex, setCurrentIndex, isPlaying, setIsPlaying, shuffleOn, setShuffleOn, togglePlayPauseRef } = usePlayer();
  const audioRef = useRef(null);

  const [repeatMode, setRepeatMode] = useState('none'); // 'none' | 'all' | 'one'
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);

  // Keep a ref so stale closures can read current isPlaying
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const currentTrack = tracks[currentIndex];
  const audioSrc = currentTrack
    ? (currentTrack.file?.startsWith('http') ? currentTrack.file : `${MEDIA_BASE}${currentTrack.file}`)
    : '';

  const getNextIndex = useCallback((current) => {
    if (shuffleOn && tracks.length > 1) {
      let n;
      do { n = Math.floor(Math.random() * tracks.length); } while (n === current);
      return n;
    }
    return (current + 1) % tracks.length;
  }, [shuffleOn, tracks.length]);

  const handleEnded = useCallback(() => {
    if (repeatMode === 'one') {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
      return;
    }
    if (repeatMode === 'none' && !shuffleOn && currentIndex === tracks.length - 1) {
      setIsPlaying(false);
      return;
    }
    setCurrentIndex(getNextIndex(currentIndex));
    setIsPlaying(true);
  }, [repeatMode, shuffleOn, currentIndex, tracks.length, getNextIndex, setCurrentIndex, setIsPlaying]);

  const handleToggle = useCallback(() => {
    if (!currentTrack) return;
    setIsPlaying((prev) => {
      const next = !prev;
      if (audioRef.current) next ? audioRef.current.play().catch(() => {}) : audioRef.current.pause();
      return next;
    });
  }, [currentTrack, setIsPlaying]);

  // Register toggle with context so PlaylistPage can trigger play/pause on track click
  useEffect(() => {
    if (!togglePlayPauseRef) return;
    togglePlayPauseRef.current = handleToggle;
    return () => { togglePlayPauseRef.current = null; };
  }, [togglePlayPauseRef, handleToggle]);

  const handleNext = useCallback(() => {
    if (!tracks.length) return;
    setCurrentIndex(getNextIndex(currentIndex));
    setIsPlaying(true);
  }, [tracks.length, currentIndex, getNextIndex, setCurrentIndex, setIsPlaying]);

  const handlePrev = useCallback(() => {
    if (!tracks.length) return;
    if (currentTime > 3) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      return;
    }
    setCurrentIndex((prev) => (prev - 1 + tracks.length) % tracks.length);
    setIsPlaying(true);
  }, [tracks.length, currentTime, setCurrentIndex, setIsPlaying]);

  const cycleRepeat = () => setRepeatMode((p) => p === 'none' ? 'all' : p === 'all' ? 'one' : 'none');

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const t    = pct * duration;
    if (audioRef.current) audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  // Load new track, auto-play if was already playing
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.load();
    setCurrentTime(0);
    if (isPlayingRef.current) audioRef.current.play().catch(() => {});
  }, [audioSrc]);

  // Play / pause toggle (without changing track)
  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  // Browser tab title + Media Session
  useEffect(() => {
    document.title = isPlaying ? 'ferl player' : 'ferl.fyi';
    return () => { document.title = 'ferl.fyi'; };
  }, [isPlaying]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !currentTrack) return;
    navigator.mediaSession.metadata = new MediaMetadata({ title: currentTrack.title, artist: currentTrack.artist, album: 'ferl' });
    navigator.mediaSession.setActionHandler('play',          () => { setIsPlaying(true);  audioRef.current?.play(); });
    navigator.mediaSession.setActionHandler('pause',         () => { setIsPlaying(false); audioRef.current?.pause(); });
    navigator.mediaSession.setActionHandler('previoustrack', handlePrev);
    navigator.mediaSession.setActionHandler('nexttrack',     handleNext);
    return () => {
      ['play','pause','previoustrack','nexttrack'].forEach((a) => navigator.mediaSession.setActionHandler(a, null));
    };
  }, [currentTrack, handlePrev, handleNext, setIsPlaying]);

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="mp">
      <div className="mp-wrapper">
      {/* Track info */}
      <div className="mp-info">
        <span className="mp-title">{currentTrack?.title ?? '—'}</span>
        <span className="mp-artist">{currentTrack?.artist ?? ''}</span>
      </div>

      {/* Controls + progress */}
      <div className="mp-center">
        <div className="mp-controls">
          <button className={`mp-btn mp-shuffle${shuffleOn ? ' mp-active' : ''}`} onClick={() => setShuffleOn((p) => !p)} title="Shuffle" disabled={!currentTrack} aria-pressed={shuffleOn}>
            <ShuffleIcon />
          </button>
          <button className="mp-btn" onClick={handlePrev} disabled={!currentTrack} title="Previous">
            <PrevIcon />
          </button>
          <button className="mp-btn mp-play-btn" onClick={handleToggle} disabled={!currentTrack} title={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <button className="mp-btn" onClick={handleNext} disabled={!currentTrack} title="Next">
            <NextIcon />
          </button>
          <button className={`mp-btn mp-repeat-btn${repeatMode !== 'none' ? ' mp-active' : ''}`} onClick={cycleRepeat} title="Repeat" disabled={!currentTrack}>
            <RepeatIcon />
            {repeatMode === 'one' && <span className="mp-repeat-one">1</span>}
          </button>
        </div>

        <div className="mp-progress-row">
          <span className="mp-time">{fmt(currentTime)}</span>
          <div className="mp-bar" onClick={handleSeek}>
            <div className="mp-bar-fill" style={{ width: `${pct}%` }} />
            <div className="mp-bar-thumb" style={{ left: `${pct}%` }} />
          </div>
          <span className="mp-time mp-time-right">{fmt(duration)}</span>
        </div>
      </div>

      {/* Right spacer — balances mp-info so center controls stay truly centered */}
      <div className="mp-right" aria-hidden="true" />
      </div>

      {currentTrack && (
        <audio
          ref={audioRef}
          src={audioSrc}
          onEnded={handleEnded}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        />
      )}
    </div>
  );
}

export default MusicPlayer;
